"use client";

import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";

import { trackPollVoted } from "@/lib/analytics/events";

export interface PollOption {
  id: string;
  label: string;
  /** Optional sub-label (e.g. "yield-focused"). */
  caption?: string;
}

interface PollProps {
  /** Stable id used for tracking + localStorage. e.g. "motivation". */
  id: string;
  question: string;
  options: readonly PollOption[];
  /** Optional intro line above the question. */
  eyebrow?: string;
  /** Shown once user votes. */
  thanksLine?: string;
}

const STORAGE_PREFIX = "rrd_poll_v1_";

/**
 * Lightweight inline poll. Renders 2-N option buttons. On click,
 * fires `poll_voted` to PostHog with the poll id + chosen option,
 * remembers the vote in localStorage so refreshes stay locked, and
 * shows a thank-you state with a soft "you and N readers chose this"
 * line (N is just visual flavor — we don't fetch live counts to
 * avoid the request cost; if real counts matter later, hook into the
 * PostHog query API).
 */
export function Poll({
  id,
  question,
  options,
  eyebrow = "Encuesta rápida",
  thanksLine = "Gracias por votar.",
}: PollProps) {
  const [voted, setVoted] = useState<string | null>(null);

  // Restore prior vote on mount.
  useEffect(() => {
    try {
      const prior = localStorage.getItem(STORAGE_PREFIX + id);
      if (prior) setVoted(prior);
    } catch {
      // localStorage unavailable (private mode, SSR boundary). No-op.
    }
  }, [id]);

  const onVote = (optionId: string) => {
    setVoted(optionId);
    try {
      localStorage.setItem(STORAGE_PREFIX + id, optionId);
    } catch {
      // ignore
    }
    trackPollVoted({ poll: id, option: optionId });
  };

  const chosen = options.find((o) => o.id === voted);

  return (
    <aside
      aria-label="Encuesta"
      className="my-12 rounded-3xl bg-cream-100 p-6 ring-1 ring-ink-200 sm:p-8"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-coral">
        {eyebrow}
      </p>
      <h3 className="mt-2 text-xl font-bold leading-tight text-ink sm:text-2xl">
        {question}
      </h3>

      {voted && chosen ? (
        <div
          className="mt-6 flex items-start gap-3 rounded-2xl bg-white p-4 ring-1 ring-emerald/30"
          role="status"
        >
          <CheckCircle2
            className="mt-0.5 h-5 w-5 shrink-0 text-emerald"
            aria-hidden
          />
          <div>
            <p className="text-sm font-semibold text-ink">
              Tu voto:{" "}
              <span className="font-bold text-coral">{chosen.label}</span>
            </p>
            <p className="mt-1 text-xs text-ink-500">{thanksLine}</p>
          </div>
        </div>
      ) : (
        <ul className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                onClick={() => onVote(option.id)}
                className="group flex h-full w-full flex-col items-start gap-1 rounded-2xl bg-white px-4 py-4 text-left ring-1 ring-ink-200 transition hover:ring-2 hover:ring-coral/60"
              >
                <span className="text-sm font-bold text-ink group-hover:text-coral">
                  {option.label}
                </span>
                {option.caption && (
                  <span className="text-xs text-ink-500">
                    {option.caption}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
